import {
    DRAWING_OBJECT_TYPE,
    DRAWING_OBJECT_PROPS,
} from "../../../common/utility/constant.js";

/** @typedef {{ k: number, x: number, y: number }} ChartTransform */

export function pointToLineDistance(px, py, start, end) {
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const lengthSquared = dx * dx + dy * dy;
    if (lengthSquared === 0) {
        return Math.hypot(px - start.x, py - start.y);
    }
    let t = ((px - start.x) * dx + (py - start.y) * dy) / lengthSquared;
    t = Math.max(0, Math.min(1, t));
    return Math.hypot(px - (start.x + t * dx), py - (start.y + t * dy));
}

export function findMultipleAndRemainder(a, b) {
    if (b === 0) throw new Error("Divisor cannot be zero");
    const multiple = Math.trunc(a / b);
    const remainder = a - multiple * b;
    return { multiple, remainder };
}

export function normalizeParallelChannel(channel) {
    let { primary, parallel } = channel;
    if (primary.start.index > primary.end.index) {
        [primary.start, primary.end] = [primary.end, primary.start];
        [parallel.start, parallel.end] = [parallel.end, parallel.start];
    }
    return { ...channel, primary, parallel };
}

export function isMoveDragMode(mode) {
    return mode === "move";
}

/**
 * Cursor while dragging a drawing object (not hover).
 * @returns {"grabbing"|"ns-resize"|"default"}
 */
export function getDragInteractionCursor(dragMode) {
    if (dragMode === "centerPrimary" || dragMode === "centerParallel") {
        return "ns-resize";
    }
    if (isMoveDragMode(dragMode)) {
        return "grabbing";
    }
    return "default";
}

/** Crosshair charts hide the OS cursor; drawing mode uses crosshair. */
export function getDrawingSurfaceCursor(isDrawingEnabled) {
    return isDrawingEnabled ? "crosshair" : "none";
}

export function isChartOverlayElement(target) {
    const el =
        target instanceof Element
            ? target
            : target instanceof Node
              ? target.parentElement
              : null;
    return Boolean(el?.closest("[data-chart-overlay]"));
}

function screenPointFromAnchor(viewport, index, tpo) {
    const { xScaleRange, yScale, transform, xOffset } = viewport;
    let sx = xScaleRange[index] + xOffset;
    let sy = yScale(tpo);
    sx = sx * transform.k + transform.x;
    sy = sy * transform.k + transform.y;
    return { x: sx, y: sy };
}

export function createHitTesting(viewport) {
    const determineDragMode = (px, py, start, end, threshold = 5) => {
        const startPt = screenPointFromAnchor(viewport, start.index, start.tpo);
        const endPt = screenPointFromAnchor(viewport, end.index, end.tpo);
        const distanceToStart = Math.hypot(px - startPt.x, py - startPt.y);
        const distanceToEnd = Math.hypot(px - endPt.x, py - endPt.y);
        const lineDistance = pointToLineDistance(px, py, startPt, endPt);
        if (distanceToStart <= threshold) return "start";
        if (distanceToEnd <= threshold) return "end";
        if (lineDistance <= threshold) return "move";
        return null;
    };

    const determineDragModeChannel = (
        px,
        py,
        start,
        end,
        threshold = 5,
        isPrimary = true,
    ) => {
        const startPt = screenPointFromAnchor(viewport, start.index, start.tpo);
        const endPt = screenPointFromAnchor(viewport, end.index, end.tpo);
        const midX = (startPt.x + endPt.x) / 2;
        const midY = (startPt.y + endPt.y) / 2;
        const distanceToStart = Math.hypot(px - startPt.x, py - startPt.y);
        const distanceToEnd = Math.hypot(px - endPt.x, py - endPt.y);
        const distanceToMid = Math.hypot(px - midX, py - midY);
        const lineDistance = pointToLineDistance(px, py, startPt, endPt);
        if (distanceToStart <= threshold) return "start";
        if (distanceToEnd <= threshold) return "end";
        if (distanceToMid <= threshold) {
            return isPrimary ? "centerPrimary" : "centerParallel";
        }
        if (lineDistance <= threshold) return "move";
        return null;
    };

    return { determineDragMode, determineDragModeChannel };
}

function applyStrokeStyle(ctx, style, zoomK) {
    if (style === "dotted") {
        ctx.setLineDash([3 / zoomK, 3 / zoomK]);
    } else if (style === "dashed") {
        ctx.setLineDash([9 / zoomK, 5 / zoomK]);
    } else {
        ctx.setLineDash([]);
    }
}

/**
 * Renders user drawings onto the overlay canvas (inside zoom transform when val === "inner").
 */
export function renderDrawingOverlay({
    ctx,
    canvasWidth,
    canvasHeight,
    viewport,
    zX,
    val = "inner",
    lines,
    parallelChannels,
    startPoint,
    endPoint,
    activeChannel,
    hexToRGBA,
    horizontalLabelStartIndex = 0,
}) {
    const { xScaleRange, yScale, transform, xOffset } = viewport;
    const offset = xOffset;
    const zoomK = transform.k;

    ctx.save();
    if (val === "inner") {
        ctx.clearRect(0, 0, canvasWidth, canvasHeight);
        ctx.translate(zX.x, zX.y);
        ctx.scale(zX.k, zX.k);
    }

    lines.forEach((line) => {
        if (
            line.type === DRAWING_OBJECT_TYPE.HORIZONTAL_LINE ||
            (line.type === DRAWING_OBJECT_TYPE.HORIZONTAL_RAY &&
                line.start.index !== -1) ||
            (line.type === DRAWING_OBJECT_TYPE.LINE &&
                line.start.index !== -1 &&
                line.end.index !== -1)
        ) {
            const xCord1 = xScaleRange[line.start.index];
            const xCord2 = xScaleRange[line.end.index];
            const yCord1 = yScale(line.start.tpo);
            const yCord2 = yScale(line.end.tpo);
            ctx.beginPath();
            ctx.lineWidth = line.width / zoomK;
            applyStrokeStyle(ctx, line.style, zoomK);
            ctx.moveTo(xCord1 + offset, yCord1);
            ctx.lineTo(xCord2 + offset, yCord2);
            ctx.strokeStyle = line.color;
            ctx.stroke();

            if (line.isSelected) {
                ctx.setLineDash([]);
                [{ x: xCord1, y: yCord1 }, { x: xCord2, y: yCord2 }].forEach(
                    (point) => {
                        ctx.beginPath();
                        ctx.arc(
                            point.x + offset,
                            point.y,
                            5 / zoomK,
                            0,
                            Math.PI * 2,
                            false,
                        );
                        ctx.strokeStyle = "white";
                        ctx.lineWidth = 2;
                        ctx.stroke();
                    },
                );
            }
        }
    });

    ctx.setLineDash([]);
    parallelChannels.forEach((channel) => {
        if (
            channel.primary.start.index === -1 ||
            channel.primary.end.index === -1
        ) {
            return;
        }
        const xCord1 = xScaleRange[channel.primary.start.index] + offset;
        const xCord2 = xScaleRange[channel.primary.end.index] + offset;
        const yCord1 = yScale(channel.primary.start.tpo);
        const yCord2 = yScale(channel.primary.end.tpo);
        const xCord11 = xScaleRange[channel.parallel.start.index] + offset;
        const xCord22 = xScaleRange[channel.parallel.end.index] + offset;
        const yCord11 = yScale(channel.parallel.start.tpo);
        const yCord22 = yScale(channel.parallel.end.tpo);
        applyStrokeStyle(ctx, channel.style, zoomK);

        ctx.beginPath();
        ctx.moveTo(xCord1, yCord1);
        ctx.lineTo(xCord2, yCord2);
        ctx.lineWidth = channel.width / zoomK;
        ctx.strokeStyle = channel.color;
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(xCord11, yCord11);
        ctx.lineTo(xCord22, yCord22);
        ctx.lineWidth = channel.width / zoomK;
        ctx.strokeStyle = channel.color;
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(xCord1, yCord1);
        ctx.lineTo(xCord2, yCord2);
        ctx.lineTo(xCord22, yCord22);
        ctx.lineTo(xCord11, yCord11);
        ctx.closePath();
        ctx.fillStyle = hexToRGBA(channel.color, 0.2);
        ctx.fill();

        if (channel.isSelected) {
            ctx.setLineDash([]);
            const centerX1 = (xCord1 + xCord2) / 2;
            const centerY1 = (yCord1 + yCord2) / 2;
            const centerX11 = (xCord11 + xCord22) / 2;
            const centerY11 = (yCord11 + yCord22) / 2;
            [
                { x: xCord1, y: yCord1 },
                { x: centerX1, y: centerY1 },
                { x: xCord2, y: yCord2 },
                { x: xCord11, y: yCord11 },
                { x: centerX11, y: centerY11 },
                { x: xCord22, y: yCord22 },
            ].forEach((point) => {
                ctx.beginPath();
                ctx.arc(point.x, point.y, 5 / zoomK, 0, Math.PI * 2, false);
                ctx.strokeStyle = "white";
                ctx.lineWidth = 2;
                ctx.stroke();
            });
        }
    });

    ctx.setLineDash([]);
    if (startPoint && endPoint) {
        ctx.beginPath();
        ctx.lineWidth = DRAWING_OBJECT_PROPS.LINE.width / zoomK;
        ctx.moveTo(
            xScaleRange[startPoint.index] + offset,
            yScale(startPoint.tpo),
        );
        ctx.lineTo(
            xScaleRange[endPoint.index] + offset,
            yScale(endPoint.tpo),
        );
        ctx.strokeStyle = DRAWING_OBJECT_PROPS.LINE.color;
        ctx.stroke();
    }

    if (activeChannel) {
        if (activeChannel.primary?.end) {
            ctx.beginPath();
            ctx.moveTo(
                xScaleRange[activeChannel.primary.start.index] + offset,
                yScale(activeChannel.primary.start.tpo),
            );
            ctx.lineTo(
                xScaleRange[activeChannel.primary.end.index] + offset,
                yScale(activeChannel.primary.end.tpo),
            );
            ctx.lineWidth = activeChannel.width / zoomK;
            ctx.strokeStyle = activeChannel.color;
            ctx.stroke();
        }
        if (activeChannel.parallel?.start && activeChannel.parallel?.end) {
            ctx.beginPath();
            ctx.moveTo(
                xScaleRange[activeChannel.parallel.start.index] + offset,
                yScale(activeChannel.parallel.start.tpo),
            );
            ctx.lineTo(
                xScaleRange[activeChannel.parallel.end.index] + offset,
                yScale(activeChannel.parallel.end.tpo),
            );
            ctx.lineWidth = activeChannel.width / zoomK;
            ctx.strokeStyle = hexToRGBA(activeChannel.color, 0.2);
            ctx.stroke();

            ctx.beginPath();
            ctx.moveTo(
                xScaleRange[activeChannel.primary.start.index] + offset,
                yScale(activeChannel.primary.start.tpo),
            );
            ctx.lineTo(
                xScaleRange[activeChannel.primary.end.index] + offset,
                yScale(activeChannel.primary.end.tpo),
            );
            ctx.lineTo(
                xScaleRange[activeChannel.parallel.end.index] + offset,
                yScale(activeChannel.parallel.end.tpo),
            );
            ctx.lineTo(
                xScaleRange[activeChannel.parallel.start.index] + offset,
                yScale(activeChannel.parallel.start.tpo),
            );
            ctx.fillStyle = hexToRGBA(activeChannel.color, 0.2);
            ctx.closePath();
            ctx.fill();
        }
    }

    lines.forEach((line) => {
        if (
            !line.text ||
            !(
                line.type === DRAWING_OBJECT_TYPE.HORIZONTAL_LINE ||
                (line.type === DRAWING_OBJECT_TYPE.HORIZONTAL_RAY &&
                    line.start.index !== -1) ||
                (line.type === DRAWING_OBJECT_TYPE.LINE &&
                    line.start.index !== -1 &&
                    line.end.index !== -1)
            )
        ) {
            return;
        }
        ctx.save();
        if (line.type === DRAWING_OBJECT_TYPE.LINE) {
            const xCord1 = xScaleRange[line.start.index];
            const xCord2 = xScaleRange[line.end.index];
            const yCord1 = yScale(line.start.tpo);
            const yCord2 = yScale(line.end.tpo);
            const midX = (xCord1 + xCord2) / 2;
            const midY = (yCord1 + yCord2) / 2;
            const angle = Math.atan2(yCord2 - yCord1, xCord2 - xCord1);
            ctx.translate(midX, midY - 12);
            ctx.rotate(angle);
            ctx.font = `${14 / zoomK}px Arial`;
            ctx.fillStyle = "white";
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.fillText(line.text, 0, 0);
        } else if (line.type === DRAWING_OBJECT_TYPE.HORIZONTAL_RAY) {
            const xCord1 = xScaleRange[line.start.index];
            const yCord1 = yScale(line.start.tpo);
            ctx.font = `${14 / zoomK}px Arial`;
            ctx.fillStyle = "white";
            ctx.fillText(line.text, xCord1 + 20, yCord1 - 8);
        } else if (line.type === DRAWING_OBJECT_TYPE.HORIZONTAL_LINE) {
            const xCord1 = xScaleRange[horizontalLabelStartIndex];
            const yCord1 = yScale(line.start.tpo);
            ctx.font = `${14 / zoomK}px Arial`;
            ctx.fillStyle = "white";
            ctx.fillText(line.text, xCord1 + 20, yCord1 - 8);
        }
        ctx.restore();
    });

    parallelChannels.forEach((channel) => {
        if (
            channel.text == null ||
            channel.primary.start.index === -1 ||
            channel.primary.end.index === -1
        ) {
            return;
        }
        ctx.save();
        const xCord1 = xScaleRange[channel.primary.start.index];
        const xCord2 = xScaleRange[channel.primary.end.index];
        const yCord1 = yScale(channel.primary.start.tpo);
        const yCord2 = yScale(channel.primary.end.tpo);
        const midX = (xCord1 + xCord2) / 2;
        const midY = (yCord1 + yCord2) / 2;
        const angle = Math.atan2(yCord2 - yCord1, xCord2 - xCord1);
        ctx.translate(midX, midY - 12);
        ctx.rotate(angle);
        ctx.font = `${14 / zoomK}px Arial`;
        ctx.fillStyle = "white";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(channel.text, 0, 0);
        ctx.restore();
    });

    ctx.restore();
}

/**
 * Mutates line in place during drag (future line chart behavior).
 */
export function dragLineDuringMove({
    line,
    dragMode,
    x,
    y,
    dragStart,
    transform,
    yScale,
    xScaleRangeLength,
    modDateList,
    historicalDataLength,
    resolveDateTime,
}) {
    const MIN_DX = 10 * transform.k;
    const dx = x - dragStart.x;
    const dy = (y - dragStart.y) / transform.k;
    const { multiple, remainder } = findMultipleAndRemainder(dx, MIN_DX);

    line.start.y = yScale(line.start.tpo) + dy;
    line.end.y = yScale(line.end.tpo) + dy;

    let index1 = line.start.index + multiple;
    let index2 = line.end.index + multiple;
    let tpo1;
    let tpo2;

    if (line.type === DRAWING_OBJECT_TYPE.HORIZONTAL_RAY) {
        index2 = xScaleRangeLength - 1;
        line.start.y = yScale(line.start.tpo) + dy;
        line.end.y = yScale(line.end.tpo) + dy;
        tpo1 = yScale.invert(line.start.y).toFixed(2);
        tpo2 = yScale.invert(line.end.y).toFixed(2);
    } else if (line.type === DRAWING_OBJECT_TYPE.HORIZONTAL_LINE) {
        index1 = 0;
        index2 = xScaleRangeLength - 1;
        line.start.y = yScale(line.start.tpo) + dy;
        line.end.y = yScale(line.end.tpo) + dy;
        tpo1 = yScale.invert(line.start.y).toFixed(2);
        tpo2 = yScale.invert(line.end.y).toFixed(2);
    } else if (line.type === DRAWING_OBJECT_TYPE.LINE) {
        if (index1 >= xScaleRangeLength - 1) index1 = xScaleRangeLength - 2;
        if (index2 >= xScaleRangeLength - 1) index2 = xScaleRangeLength - 2;
        if (dragMode === "start") {
            index2 = line.end.index;
            line.start.y = yScale(line.start.tpo) + dy;
            tpo1 = yScale.invert(line.start.y).toFixed(2);
            tpo2 = line.end.tpo;
        } else if (dragMode === "end") {
            index1 = line.start.index;
            line.end.y = yScale(line.end.tpo) + dy;
            tpo2 = yScale.invert(line.end.y).toFixed(2);
            tpo1 = line.start.tpo;
        } else {
            line.start.y = yScale(line.start.tpo) + dy;
            line.end.y = yScale(line.end.tpo) + dy;
            tpo1 = yScale.invert(line.start.y).toFixed(2);
            tpo2 = yScale.invert(line.end.y).toFixed(2);
        }
    }

    line.start.index = index1;
    line.start.tpo = tpo1;
    line.end.index = index2;
    line.end.tpo = tpo2;

    if (
        line.type === DRAWING_OBJECT_TYPE.LINE ||
        line.type === DRAWING_OBJECT_TYPE.HORIZONTAL_RAY
    ) {
        const { date, time } = resolveDateTime(index1);
        line.start.date = date;
        line.start.time = time;
    }
    if (line.type === DRAWING_OBJECT_TYPE.LINE) {
        const endMeta = resolveDateTime(index2);
        line.end.date = endMeta.date;
        line.end.time = endMeta.time;
        const startMeta = resolveDateTime(index1);
        line.start.date = startMeta.date;
        line.start.time = startMeta.time;
    }

    return { dragStart: { x: x - remainder, y } };
}

export function dragChannelDuringMove({
    channel,
    dragMode,
    x,
    y,
    dragStart,
    transform,
    yScale,
    xScaleRangeLength,
    modDateList,
    historicalDataLength,
    resolveDateTime,
}) {
    const MIN_DX = 10 * transform.k;
    const dx = x - dragStart.x;
    const dy = (y - dragStart.y) / transform.k;
    const { multiple, remainder } = findMultipleAndRemainder(dx, MIN_DX);

    channel.primary.start.y = yScale(channel.primary.start.tpo) + dy;
    channel.primary.end.y = yScale(channel.primary.end.tpo) + dy;
    channel.parallel.start.y = yScale(channel.parallel.start.tpo) + dy;
    channel.parallel.end.y = yScale(channel.parallel.end.tpo) + dy;

    let index1 =
        dragMode === "start" || dragMode === "move"
            ? channel.primary.start.index + multiple
            : channel.primary.start.index;
    let index2 =
        dragMode === "end" || dragMode === "move"
            ? channel.primary.end.index + multiple
            : channel.primary.end.index;

    if (index1 >= xScaleRangeLength - 1) index1 = xScaleRangeLength - 2;
    if (index2 >= xScaleRangeLength - 1) index2 = xScaleRangeLength - 2;

    let tpo1 =
        dragMode === "start" || dragMode === "move"
            ? yScale.invert(channel.primary.start.y).toFixed(2)
            : channel.primary.start.tpo;
    let tpo2 =
        dragMode === "end" || dragMode === "move"
            ? yScale.invert(channel.primary.end.y).toFixed(2)
            : channel.primary.end.tpo;
    let tpo11 =
        dragMode === "start" || dragMode === "move"
            ? yScale.invert(channel.parallel.start.y).toFixed(2)
            : channel.parallel.start.tpo;
    let tpo22 =
        dragMode === "end" || dragMode === "move"
            ? yScale.invert(channel.parallel.end.y).toFixed(2)
            : channel.parallel.end.tpo;

    if (dragMode === "centerPrimary") {
        tpo1 = yScale.invert(channel.primary.start.y).toFixed(2);
        tpo2 = yScale.invert(channel.primary.end.y).toFixed(2);
    } else if (dragMode === "centerParallel") {
        tpo11 = yScale.invert(channel.parallel.start.y).toFixed(2);
        tpo22 = yScale.invert(channel.parallel.end.y).toFixed(2);
    }

    const meta1 = resolveDateTime(index1);
    const meta2 = resolveDateTime(index2);

    if (channel.type === DRAWING_OBJECT_TYPE.PARALLEL_CHANNEL) {
        channel.primary.start.index = index1;
        channel.primary.start.tpo = tpo1;
        channel.primary.start.date = meta1.date;
        channel.primary.start.time = meta1.time;
        channel.primary.end.index = index2;
        channel.primary.end.tpo = tpo2;
        channel.primary.end.date = meta2.date;
        channel.primary.end.time = meta2.time;
        channel.parallel.start.index = index1;
        channel.parallel.start.tpo = tpo11;
        channel.parallel.start.date = meta1.date;
        channel.parallel.start.time = meta1.time;
        channel.parallel.end.index = index2;
        channel.parallel.end.tpo = tpo22;
        channel.parallel.end.date = meta2.date;
        channel.parallel.end.time = meta2.time;
    } else if (channel.type === DRAWING_OBJECT_TYPE.HORIZONTAL_CHANNEL) {
        channel.primary.start.index = index1;
        channel.primary.start.tpo = tpo1;
        channel.primary.start.date = meta1.date;
        channel.primary.start.time = meta1.time;
        channel.primary.end.index = index2;
        channel.primary.end.tpo = tpo1;
        channel.primary.end.date = meta2.date;
        channel.primary.end.time = meta2.time;
        channel.parallel.start.index = index1;
        channel.parallel.start.tpo = tpo11;
        channel.parallel.start.date = meta1.date;
        channel.parallel.start.time = meta1.time;
        channel.parallel.end.index = index2;
        channel.parallel.end.tpo = tpo11;
        channel.parallel.end.date = meta1.date;
        channel.parallel.end.time = meta1.time;
    }

    Object.assign(channel, normalizeParallelChannel(channel));
    return { dragStart: { x: x - remainder, y } };
}

export function resolveDrawingDateTime(index, modDateList, historicalDataLength, historicalData) {
    const [dateString, timeString] = modDateList[index].split(" ");
    const date =
        index < historicalDataLength
            ? historicalData.config.data[index].date
            : dateString;
    const time =
        index < historicalDataLength
            ? historicalData.config.data[index].time
            : timeString;
    return { date, time };
}

export function isLineDrawingType(type) {
    return (
        type === DRAWING_OBJECT_TYPE.LINE ||
        type === DRAWING_OBJECT_TYPE.HORIZONTAL_LINE ||
        type === DRAWING_OBJECT_TYPE.HORIZONTAL_RAY
    );
}

export function isChannelDrawingType(type) {
    return (
        type === DRAWING_OBJECT_TYPE.PARALLEL_CHANNEL ||
        type === DRAWING_OBJECT_TYPE.HORIZONTAL_CHANNEL
    );
}

export function isDrawingKeyboardTypingTarget(target) {
    const el = target;
    const tag = el?.tagName;
    return (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        el?.isContentEditable
    );
}

export function isDrawingToolbarElement(target) {
    return target?.closest?.('[id^="toolbar"]') != null;
}

/** Ignore click/event args mistakenly passed as the delete target. */
export function resolveExplicitDrawingDeleteTarget(explicitTarget, resolveFallback) {
    if (
        explicitTarget?.id != null &&
        (isLineDrawingType(explicitTarget.type) ||
            isChannelDrawingType(explicitTarget.type))
    ) {
        return explicitTarget;
    }
    return resolveFallback();
}

/** Prefer explicit selection, then drag ref, then isSelected flags on stores. */
export function resolveDrawingDeleteTarget({
    selectedObject,
    dragLine,
    lines,
    parallelChannels,
}) {
    if (selectedObject?.id != null) return selectedObject;
    if (dragLine?.id != null) return dragLine;
    const selectedLine = lines?.find((line) => line.isSelected);
    if (selectedLine) return selectedLine;
    return parallelChannels?.find((channel) => channel.isSelected) ?? null;
}

/** True when Escape should cancel drawing / selection, not other UI. */
export function shouldHandleDrawingEscape({
    isDrawingEnabled,
    isDrawing,
    isDrawingChannel,
    isPreviewing,
    selectedObject,
    lines,
    parallelChannels,
}) {
    if (isDrawingEnabled) return true;
    if (isDrawing || isDrawingChannel || isPreviewing) return true;
    if (selectedObject?.id != null) return true;
    if (lines?.some((line) => line.isSelected)) return true;
    if (parallelChannels?.some((channel) => channel.isSelected)) return true;
    return false;
}

/** Mutates line/channel ref arrays. Returns false if nothing removed. */
export function removeDrawingObjectFromRefs({
    linesRef,
    parallelChannelsRef,
    target,
}) {
    if (!target?.id && target?.id !== 0) return false;
    const targetId = String(target.id);
    if (isLineDrawingType(target.type)) {
        const next = linesRef.current.filter(
            (line) => String(line.id) !== targetId,
        );
        if (next.length === linesRef.current.length) return false;
        linesRef.current = next;
        return true;
    }
    if (isChannelDrawingType(target.type)) {
        const next = parallelChannelsRef.current.filter(
            (channel) => String(channel.id) !== targetId,
        );
        if (next.length === parallelChannelsRef.current.length) return false;
        parallelChannelsRef.current = next;
        return true;
    }
    return false;
}

/**
 * Delete / Backspace / Escape for chart drawing tools.
 * @returns {{ handled: boolean }}
 */
export function handleDrawingKeyboardDown(event, options = {}) {
    if (event.__vtDrawingKeyHandled) {
        return { handled: true };
    }
    if (isDrawingKeyboardTypingTarget(event.target)) {
        const inDrawingToolbar = isDrawingToolbarElement(event.target);
        const key = event.key;
        const allowDeleteFromToolbar =
            inDrawingToolbar && key === "Delete";
        if (!allowDeleteFromToolbar) {
            return { handled: false };
        }
    }

    const { key } = event;
    const {
        selectedObject,
        dragLine,
        lines,
        parallelChannels,
        onEscape,
        onDelete,
        shouldHandleEscape,
    } = options;

    if (key === "Escape") {
        if (!onEscape) {
            return { handled: false };
        }
        if (
            shouldHandleEscape &&
            !shouldHandleEscape()
        ) {
            return { handled: false };
        }
        onEscape();
        event.__vtDrawingKeyHandled = true;
        return { handled: true };
    }

    if (key === "Delete" || key === "Backspace") {
        const target = resolveDrawingDeleteTarget({
            selectedObject,
            dragLine,
            lines,
            parallelChannels,
        });
        if (!target) {
            return { handled: false };
        }
        event.preventDefault();
        onDelete?.(target);
        event.__vtDrawingKeyHandled = true;
        return { handled: true };
    }

    return { handled: false };
}
